import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { artwork, artworkForInstance } from "../HomepageMascot/artwork";
import mascotStyles from "../HomepageMascot/mascot.css?inline";
import visitorStyles from "./visitors.css?inline";
import {
  MASCOT_SCENE_EVENT,
  MASCOT_ARRIVED_EVENT,
  MASCOT_INTERACTION_EVENT,
} from "../HomepageMascot/sceneEvents";
import { getSkyChaseGeometry } from "../HomepageMascot/chaseGeometry";
import cls from "./styles.module.scss";

type Phase =
  | "waiting"
  | "peeking"
  | "watching"
  | "shoo"
  | "sweep-right"
  | "sweep-left"
  | "fleeing"
  | "done";

// Each leg uses the existing guide's spring flight and actual arrival signal.
const nextArrival: Partial<Record<Phase, Phase>> = {
  shoo: "sweep-right",
  "sweep-right": "sweep-left",
  "sweep-left": "fleeing",
};

export function SkyVisitors() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [phase, setPhase] = useState<Phase>("waiting");
  const [chaseReady, setChaseReady] = useState(false);
  const phaseRef = useRef<Phase>(phase);
  const chaseReadyRef = useRef(false);
  const hintOpenRef = useRef(false);
  const played = useRef(false);

  useLayoutEffect(() => {
    phaseRef.current = phase;
    const section = ref.current?.closest<HTMLElement>("#home-sky-discovery");
    if (section) section.dataset.skyVisitorsPhase = phase;
  }, [phase]);

  useEffect(() => {
    const host = ref.current;
    const section = host?.closest<HTMLElement>("#home-sky-discovery");
    const scroll = section?.closest<HTMLElement>("[data-site-scroll]");
    if (!host || !section || !scroll) return;
    const shadow = host.shadowRoot || host.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = mascotStyles + visitorStyles;
    const fragment = document.createElement("div");
    fragment.className = "visitors";
    fragment.innerHTML = [0, 1, 2]
      .map(
        (index) =>
          `<div class="visitor visitor-${index}" data-sky-visitor="${index}"><div class="mascot" style="${
            artwork.style
          }">${artworkForInstance(`sky-visitor-${index}`)}</div></div>`
      )
      .join("");
    shadow.replaceChildren(style, fragment);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const finish = () => {
      if (played.current && phaseRef.current !== "done") setPhase("done");
    };
    const interrupt = () => {
      played.current = true;
      // A hint-close signal may follow an overlay/visibility interruption in
      // the same turn. Do not let it resume a scene already marked finished.
      phaseRef.current = "done";
      setPhase("done");
    };
    const hasRoomToChase = () => {
      if (reduced.matches || document.hidden || hintOpenRef.current)
        return false;
      const header = document.querySelector<HTMLElement>("[data-site-header]");
      const chat = scroll.querySelector<HTMLElement>("[data-chat-expanded]");
      const layout = chat?.dataset.chatLayout;
      if (
        header?.dataset.menuOpen === "true" ||
        layout === "fullscreen" ||
        (chat?.dataset.chatExpanded === "true" &&
          ["mobile", "docked"].includes(layout || ""))
      )
        return false;
      const bounds = scroll.getBoundingClientRect();
      const top =
        Math.max(bounds.top, header?.getBoundingClientRect().bottom || 0) + 12;
      let bottom = Math.min(innerHeight, bounds.bottom) - 16;
      if (chat && ["mobile", "docked"].includes(layout || ""))
        bottom = Math.min(bottom, chat.getBoundingClientRect().top - 12);
      return Boolean(
        getSkyChaseGeometry(
          section,
          { top, bottom },
          Math.min(innerWidth < 680 ? 128 : 176, innerWidth - 32)
        )
      );
    };
    const syncChase = () => {
      const ready = hasRoomToChase();
      chaseReadyRef.current = ready;
      setChaseReady(ready);
      // Scrolling away suspends the guide's chase, not the visitors' visit.
      if (!ready && nextArrival[phaseRef.current]) setPhase("watching");
      if (ready && !played.current) {
        played.current = true;
        setPhase("peeking");
      }
    };
    const sceneChanged = () => syncChase();
    const guideInteraction = (event: Event) => {
      const hintOpen = (event as CustomEvent<{ hintOpen?: boolean }>).detail
        ?.hintOpen;
      if (typeof hintOpen === "boolean") {
        hintOpenRef.current = hintOpen;
        // Reading suspends an unfinished chase like scrolling out of view.
        // Visitors keep hovering; a retreat already under way can finish.
        syncChase();
        return;
      }
      // Reading in the previous block also has priority once guests emerged.
      // Reading elsewhere before their visit must not cancel the future visit.
      if (played.current || event.target === section) interrupt();
    };
    const arrived = () => {
      const next = nextArrival[phaseRef.current];
      if (next && hasRoomToChase()) setPhase(next);
    };
    const preferenceChanged = () => {
      if (reduced.matches) {
        played.current = true;
        setPhase("done");
      } else syncChase();
    };
    const visibilityChanged = () => {
      if (document.hidden) finish();
    };
    section.addEventListener(MASCOT_SCENE_EVENT, sceneChanged);
    section.addEventListener(MASCOT_ARRIVED_EVENT, arrived);
    scroll.addEventListener(MASCOT_INTERACTION_EVENT, guideInteraction, true);
    scroll.addEventListener("scroll", syncChase, { passive: true });
    scroll.addEventListener("load", syncChase, true);
    window.addEventListener("resize", syncChase, { passive: true });
    document.addEventListener("visibilitychange", visibilityChanged);
    reduced.addEventListener("change", preferenceChanged);
    const layoutObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(syncChase);
    for (const node of [
      section,
      scroll,
      document.querySelector("[data-site-header]"),
      section.querySelector("#sky-discovery-title"),
    ])
      if (node) layoutObserver?.observe(node);
    const overlayObserver = new MutationObserver(syncChase);
    const header = document.querySelector("[data-site-header]");
    const chat = scroll.querySelector("[data-chat-expanded]");
    if (header)
      overlayObserver.observe(header, {
        attributes: true,
        attributeFilter: ["data-menu-open"],
      });
    if (chat) {
      layoutObserver?.observe(chat);
      overlayObserver.observe(chat, {
        attributes: true,
        attributeFilter: ["data-chat-expanded", "data-chat-layout"],
      });
    }
    preferenceChanged();
    return () => {
      section.removeEventListener(MASCOT_SCENE_EVENT, sceneChanged);
      section.removeEventListener(MASCOT_ARRIVED_EVENT, arrived);
      scroll.removeEventListener(
        MASCOT_INTERACTION_EVENT,
        guideInteraction,
        true
      );
      scroll.removeEventListener("scroll", syncChase);
      scroll.removeEventListener("load", syncChase, true);
      window.removeEventListener("resize", syncChase);
      document.removeEventListener("visibilitychange", visibilityChanged);
      reduced.removeEventListener("change", preferenceChanged);
      layoutObserver?.disconnect();
      overlayObserver.disconnect();
      delete section.dataset.skyVisitorsPhase;
      shadow.replaceChildren();
    };
  }, []);

  const canStartChase = phase === "watching" && chaseReady;
  useEffect(() => {
    const next: Partial<Record<Phase, [number, Phase]>> = {
      peeking: [1100, "watching"],
      watching: canStartChase ? [1200, "shoo"] : undefined,
      // Visitors stay until the actual guide can return and reach them.
      // Allow the delayed, staggered retreat to finish before hiding its host.
      fleeing: [2700, "done"],
    };
    const transition = next[phase];
    if (!transition) return;
    const timer = window.setTimeout(() => {
      if (phase === "watching" && !chaseReadyRef.current) return;
      setPhase(transition[1]);
    }, transition[0]);
    return () => window.clearTimeout(timer);
  }, [phase, canStartChase]);

  return (
    <div
      ref={ref}
      className={cls.visitors}
      data-sky-visitors
      data-phase={phase}
      hidden={phase === "done"}
      aria-hidden="true"
    />
  );
}
